import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { dropInjectedScriptError } from '@wundo/client/dropInjectedScriptError.ts';
import { sentryRootErrorHandlers } from '@wundo/client/sentryRootErrorHandlers.ts';
import { routeTree } from './routeTree.gen';
import { RouteError } from './components/RouteError.tsx';
import { createQueryClient } from './lib/queryClient.ts';
import { SENTRY_DSN } from './lib/sentry.ts';
import './index.css';

/*
 * Reporting is on for deployed builds only: the pipeline sets VITE_ENVIRONMENT
 * to prod or stage, a local `vite build` leaves it empty. No session replay
 * here: this console is only ever used by us, and the customer app's guard
 * discards our sessions for exactly that reason.
 */
Sentry.init({
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  release: import.meta.env.VITE_APP_VERSION,
  environment: import.meta.env.VITE_ENVIRONMENT,
  enabled: Boolean(import.meta.env.VITE_ENVIRONMENT),
  beforeSend: dropInjectedScriptError,
});

/* `router` is read only once a query fails, by which point it exists. */
const queryClient = createQueryClient(() => void router.invalidate());

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultErrorComponent: RouteError,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!, sentryRootErrorHandlers()).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
